import { Controller } from "@hotwired/stimulus"
import ChatRoomChannel from "../channels/chat_room_channel"

export default class extends Controller {
  static targets = ["messages", "input", "typingIndicator", "message", "unreadData"]
  static values = { roomId: Number, currentMemberId: String, isAdmin: { type: Boolean, default: false } }

  connect() {
    this.typingUsers = new Set()
    this.isComposing = false  // Track Korean IME composition state
    this.hasConnectedOnce = false
    this.fetchingMissed = false
    this.hiddenAt = null

    this.channel = new ChatRoomChannel(this.roomIdValue, {
      onConnected: () => this.onConnected(),
      onDisconnected: () => this.onDisconnected(),
      onMessage: (message) => this.onMessage(message),
      onTyping: (mbId, nickname) => this.onTyping(mbId, nickname),
      onStopTyping: (mbId) => this.onStopTyping(mbId),
      onReadUpdate: (readTimes, onlineMembers) => this.onReadUpdate(readTimes, onlineMembers),
      onPresence: (mbId, status, onlineMembers) => this.onPresence(mbId, status, onlineMembers),
      onReactionUpdate: (messageId, reactions) => this.onReactionUpdate(messageId, reactions),
      onMarketplaceStatus: (product) => this.onMarketplaceStatus(product),
      onMessageError: (error, content) => this.onMessageError(error, content)
    }).connect()

    this.onlineMembers = new Set()

    this.scrollToBottom()

    // Handle Korean IME composition
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("compositionstart", () => {
        this.isComposing = true
      })
      this.inputTarget.addEventListener("compositionend", () => {
        this.isComposing = false
      })
    }

    // Close reaction pickers and delete menus on outside click
    this.boundCloseReactionPickers = (e) => {
      if (!e.target.closest('.reaction-picker') && !e.target.closest('[data-action*="toggleReactionPicker"]')) {
        this.closeAllReactionPickers()
      }
      if (!e.target.closest('.delete-menu') && !e.target.closest('[data-action*="toggleDeleteMenu"]')) {
        this.closeAllDeleteMenus()
      }
    }
    document.addEventListener("click", this.boundCloseReactionPickers)

    // Initialize reactions tracking (populated from DOM + API responses)
    // Structure: { messageId: { emoji: { count, mine } } }
    this.reactions = {}
    this.initializeReactionsFromDOM()

    // Visibility / network recovery: fetch missed messages when returning from background
    this.boundHandleVisibility = () => this.handleVisibilityChange()
    this.boundHandleOnline = () => this.fetchMissedMessages()
    document.addEventListener("visibilitychange", this.boundHandleVisibility)
    window.addEventListener("online", this.boundHandleOnline)

    // Context menu for admin/owner (right-click + long press)
    if (this.isAdminValue) {
      this.longPressTimer = null
      this.boundContextMenu = (e) => this.handleContextMenu(e)
      this.boundTouchStart = (e) => this.handleTouchStart(e)
      this.boundTouchEnd = () => this.handleTouchEnd()
      this.boundTouchMove = () => this.handleTouchEnd()
      this.boundCloseModal = (e) => {
        if (e.target.id === "read-status-overlay") this.closeReadStatusModal()
      }

      if (this.hasMessagesTarget) {
        this.messagesTarget.addEventListener("contextmenu", this.boundContextMenu)
        this.messagesTarget.addEventListener("touchstart", this.boundTouchStart, { passive: true })
        this.messagesTarget.addEventListener("touchend", this.boundTouchEnd)
        this.messagesTarget.addEventListener("touchmove", this.boundTouchMove)
      }
    }

    // Wire up link preview for input URL detection
    if (this.hasInputTarget) {
      const lpEl = this.element.querySelector('[data-controller~="link-preview"]')
      if (lpEl) {
        // Defer to next tick so the link-preview controller has connected
        requestAnimationFrame(() => {
          const lpController = this.application.getControllerForElementAndIdentifier(lpEl, "link-preview")
          if (lpController) {
            this._linkPreviewController = lpController
            lpController.watchInput(this.inputTarget, this.inputTarget.closest("form"))
          }
        })
      }
    }
  }

  disconnect() {
    this.channel?.disconnect()
    if (this.boundCloseReactionPickers) {
      document.removeEventListener("click", this.boundCloseReactionPickers)
    }
    if (this.boundHandleVisibility) {
      document.removeEventListener("visibilitychange", this.boundHandleVisibility)
    }
    if (this.boundHandleOnline) {
      window.removeEventListener("online", this.boundHandleOnline)
    }
    if (this.hasMessagesTarget) {
      this.messagesTarget.removeEventListener("contextmenu", this.boundContextMenu)
      this.messagesTarget.removeEventListener("touchstart", this.boundTouchStart)
      this.messagesTarget.removeEventListener("touchend", this.boundTouchEnd)
      this.messagesTarget.removeEventListener("touchmove", this.boundTouchMove)
    }
    if (this._linkPreviewController && this.hasInputTarget) {
      this._linkPreviewController.stopWatchingInput(this.inputTarget)
    }
    this.closeReadStatusModal()
  }

  // Called when a new message is added via Turbo Stream
  messageTargetConnected(element) {
    const senderId = element.dataset.senderId
    const isMine = senderId === this.currentMemberIdValue

    // Update message alignment and styling based on sender
    // This is needed because Turbo Stream broadcasts don't know the current user
    if (isMine) {
      // Fix alignment
      element.classList.add("justify-end")
      element.classList.remove("justify-start")

      // Fix bubble styling - find the message bubble div
      const bubble = element.querySelector(".rounded-2xl.bg-white, .rounded-2xl.border-gray-200")
      if (bubble) {
        bubble.classList.remove("bg-white", "text-gray-900", "border", "border-gray-200")
        bubble.classList.add("bg-red-500", "text-white")

        // Fix mention styling inside own message
        const mentions = bubble.querySelectorAll(".mention")
        mentions.forEach(m => {
          m.classList.remove("bg-red-100", "text-red-700")
          m.classList.add("bg-red-400", "text-white")
        })

        // Fix reply preview styling inside own message
        const replyPreview = bubble.querySelector(".rounded-xl.bg-gray-100")
        if (replyPreview) {
          replyPreview.classList.remove("bg-gray-100")
          replyPreview.classList.add("bg-red-600/30")
          replyPreview.querySelectorAll(".text-gray-600").forEach(el => {
            el.classList.remove("text-gray-600")
            el.classList.add("text-red-100")
          })
          replyPreview.querySelectorAll(".text-gray-500").forEach(el => {
            el.classList.remove("text-gray-500")
            el.classList.add("text-red-200")
          })
        }
      }

      // Hide sender name and avatar for own messages
      const avatar = element.querySelector(".flex-shrink-0.mr-2")
      if (avatar) avatar.remove()

      const senderName = element.querySelector("p.text-xs.text-gray-500.mb-1.font-medium")
      if (senderName) senderName.remove()

      // Align timestamp to right
      const timestamp = element.querySelector(".flex.items-center.mt-1")
      if (timestamp) timestamp.classList.add("justify-end")

      // Move action buttons from right side to left side
      const actions = element.querySelector(".message-actions")
      if (actions) {
        actions.classList.remove("ml-2")
        actions.classList.add("mr-2")
        // Move before the content div (first child of element)
        element.insertBefore(actions, element.firstChild)
      }
    }

    // Scroll to bottom after a short delay to ensure rendering is complete
    setTimeout(() => this.scrollToBottom(), 50)

    // Mark as read if window is focused
    if (document.hasFocus() && this.channel) {
      this.channel.markRead()
    }
  }

  onConnected() {
    console.log("Chat connected")
    if (this.hasConnectedOnce) {
      // Reconnection (e.g. after mobile background/sleep) — fetch missed messages
      this.fetchMissedMessages()
    }
    this.hasConnectedOnce = true
    this.channel.markRead()
  }

  onDisconnected() {
    console.log("Chat disconnected")
  }

  handleVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this.hiddenAt = Date.now()
    } else {
      // Page became visible again
      if (this.hiddenAt && (Date.now() - this.hiddenAt) > 5000) {
        this.fetchMissedMessages()
      }
      this.hiddenAt = null
      if (document.hasFocus() && this.channel) {
        this.channel.markRead()
      }
    }
  }

  getLastMessageId() {
    if (!this.hasMessagesTarget) return null
    const msgs = this.messagesTarget.querySelectorAll("[data-message-id]")
    if (msgs.length === 0) return null
    return msgs[msgs.length - 1].dataset.messageId
  }

  async fetchMissedMessages() {
    if (this.fetchingMissed) return
    const lastId = this.getLastMessageId()
    if (!lastId) return

    this.fetchingMissed = true
    try {
      const response = await fetch(
        `/api/v1/chat/rooms/${this.roomIdValue}/messages?after=${lastId}&limit=50`,
        { headers: { "Accept": "application/json" }, credentials: "same-origin" }
      )
      if (!response.ok) return

      const messages = await response.json()
      let added = 0
      messages.forEach(msg => {
        if (document.querySelector(`[data-message-id="${msg.id}"]`)) return
        this.appendMessage({ ...msg, sender_nickname: msg.sender?.mb_nick })
        added++
      })

      if (added > 0) {
        this.scrollToBottom()
        if (this.channel) this.channel.markRead()
      }
    } catch (error) {
      console.error("Failed to fetch missed messages:", error)
    } finally {
      this.fetchingMissed = false
    }
  }

  onMessage(message) {
    // Messages are rendered by Turbo Stream (broadcast_append_to) with full server-side
    // styling (AI purple bubbles, markdown links, etc.). The messageTargetConnected callback
    // handles scrolling and read marking when Turbo appends the element.
    // We only use this handler for non-Turbo fallback (e.g., if Turbo Stream didn't fire).

    // Bot messages (ai/doc) are always delivered by Turbo Stream with full styling.
    // Skip JS fallback to avoid duplicate plain-text rendering from race conditions.
    const isBotMessage = ["ai", "doc", "system"].includes(message.sender_mb_id)
    if (isBotMessage) {
      if (document.hasFocus() && this.channel) {
        this.channel.markRead()
      }
      return
    }

    setTimeout(() => {
      const existing = document.querySelector(`[data-message-id="${message.id}"]`)
      if (!existing) {
        // Turbo Stream didn't deliver - use JS fallback
        this.appendMessage(message)
        this.scrollToBottom()
      }
      if (document.hasFocus() && this.channel) {
        this.channel.markRead()
      }
    }, 500)
  }

  onTyping(mbId, nickname) {
    if (mbId === this.currentMemberIdValue) return

    this.typingUsers.add(nickname)
    this.updateTypingIndicator()
  }

  onStopTyping(mbId) {
    // Remove from typing users (we'd need to track by mbId properly)
    this.typingUsers.clear()
    this.updateTypingIndicator()
  }

  updateTypingIndicator() {
    if (!this.hasTypingIndicatorTarget) return

    if (this.typingUsers.size > 0) {
      const names = Array.from(this.typingUsers).join(", ")
      this.typingIndicatorTarget.textContent = `${names}님이 입력 중...`
      this.typingIndicatorTarget.classList.remove("hidden")
    } else {
      this.typingIndicatorTarget.classList.add("hidden")
    }
  }

  sendMessage(event) {
    event.preventDefault()
    if (this.element.dataset.marketplaceClosed === "true") return

    const content = this.inputTarget.value.trim()
    if (!content) return

    // Send with reply info if replying
    this.channel.sendMessage(content, "text", this.replyToId || null)

    this.inputTarget.value = ""
    this.inputTarget.style.height = "38px"  // Reset height after sending
    this.hideReplyPreview()  // Clear reply state
    this.channel.stopTyping()
    this._linkPreviewController?.clearInputPreview()
  }

  handleInput() {
    if (this.element.dataset.marketplaceClosed === "true") return
    this.channel.typing()
  }

  onMarketplaceStatus(product) {
    this.element.dataset.marketplaceClosed = String(product.chat_closed)
    this.element.querySelectorAll("[data-marketplace-composer]").forEach(fieldset => { fieldset.disabled = product.chat_closed })
  }

  onMessageError(error, content) {
    if (content && this.hasInputTarget) {
      this.inputTarget.value = [content, this.inputTarget.value].filter(Boolean).join("\n")
    }
    alert(error)
  }

  autoResize(event) {
    const textarea = event.target
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "38px"
    // Set new height based on content, max 3 lines (~90px)
    const newHeight = Math.min(textarea.scrollHeight, 90)
    textarea.style.height = `${newHeight}px`
  }

  handleKeydown(event) {
    // Don't send while Korean IME is composing
    if (event.key === "Enter" && !event.shiftKey && !this.isComposing) {
      event.preventDefault()
      this.sendMessage(event)
    }
  }

  appendMessage(message) {
    if (!this.hasMessagesTarget) return

    const isMine = message.sender_mb_id === this.currentMemberIdValue
    const isAi = message.sender_mb_id === "ai"
    const isDoc = message.sender_mb_id === "doc"
    const time = new Date(message.created_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })
    const formattedContent = this.formatMessageContent(message.content, { isAi, isDoc })

    let html
    if (isAi) {
      html = `
        <div class="flex mb-4 chat-message group items-end justify-start" data-message-id="${message.id}" data-sender-id="ai">
          <div class="flex-shrink-0 mr-2">
            <div class="h-9 w-9 rounded-full flex items-center justify-center shadow-sm bg-gradient-to-br from-violet-500 to-purple-600">
              <svg class="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
              </svg>
            </div>
          </div>
          <div class="max-w-[70%] lg:max-w-md xl:max-w-lg min-w-0 shrink">
            <p class="text-xs text-purple-600 dark:text-purple-400 mb-1 font-medium">AI 어시스턴트</p>
            <div class="bg-purple-50 dark:bg-purple-900/30 text-gray-900 dark:text-gray-100 border border-purple-200 dark:border-purple-700 rounded-2xl px-4 py-2.5 shadow-sm">
              <p class="text-sm whitespace-pre-wrap break-words">${formattedContent}</p>
            </div>
            <p class="text-xs text-gray-400 mt-1">${time}</p>
          </div>
        </div>`
    } else if (isDoc) {
      html = `
        <div class="flex mb-4 chat-message group items-end justify-start" data-message-id="${message.id}" data-sender-id="doc">
          <div class="flex-shrink-0 mr-2">
            <div class="h-9 w-9 rounded-full flex items-center justify-center shadow-sm bg-gradient-to-br from-emerald-500 to-teal-600">
              <svg class="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          <div class="max-w-[70%] lg:max-w-md xl:max-w-lg min-w-0 shrink">
            <p class="text-xs text-emerald-600 dark:text-emerald-400 mb-1 font-medium">문서 검색</p>
            <div class="bg-emerald-50 dark:bg-emerald-900/30 text-gray-900 dark:text-gray-100 border border-emerald-200 dark:border-emerald-700 rounded-2xl px-4 py-2.5 shadow-sm">
              <p class="text-sm whitespace-pre-wrap break-words">${formattedContent}</p>
            </div>
            <p class="text-xs text-gray-400 mt-1">${time}</p>
          </div>
        </div>`
    } else {
      html = `
        <div class="flex mb-4 chat-message group items-end ${isMine ? 'justify-end' : 'justify-start'}" data-message-id="${message.id}" data-sender-id="${this.escapeHtml(message.sender_mb_id)}">
          ${!isMine ? `
            <div class="flex-shrink-0 mr-2">
              <div class="h-9 w-9 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center shadow-sm">
                <span class="text-sm font-medium text-gray-600 dark:text-gray-300">${this.escapeHtml((message.sender_nickname || "?")[0])}</span>
              </div>
            </div>
          ` : ''}
          <div class="max-w-[70%] lg:max-w-md xl:max-w-lg min-w-0 shrink">
            ${!isMine ? `<p class="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">${this.escapeHtml(message.sender_nickname || message.sender_mb_id)}</p>` : ''}
            <div class="${isMine ? 'bg-red-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700'} rounded-2xl px-4 py-2.5 shadow-sm">
              ${this.renderMessageBody(message, isMine)}
            </div>
            <p class="text-xs text-gray-400 mt-1 ${isMine ? 'text-right' : ''}">${time}</p>
          </div>
        </div>`
    }

    this.messagesTarget.insertAdjacentHTML("beforeend", html)
  }

  formatMessageContent(content, options = {}) {
    if (!content) return ""

    // Escape HTML first
    let formatted = this.escapeHtml(content)

    // Process markdown-style bold **text**
    formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

    // Process markdown-style links [text](/path) - used by AI sources
    formatted = formatted.replace(/\[([^\]]+)\]\((\/boards\/[^)]+)\)/g, (match, text, path) => {
      return `<a href="${this.escapeHtml(path)}" class="text-purple-600 hover:text-purple-800 underline" data-turbo-frame="_top">${this.escapeHtml(text)}</a>`
    })

    // Process @mentions - wrap in styled span
    formatted = formatted.replace(/@([가-힣a-zA-Z0-9_]+)/g, (match) => {
      return `<span class="mention bg-red-100 text-red-700 px-1 rounded">${match}</span>`
    })

    // Process [[post references]] - display title only, truncated to ~10 chars
    formatted = formatted.replace(/\[\[([^\]]+)\]\]/g, (match, reference) => {
      const parts = reference.split(":")
      const title = parts.length === 2 ? parts[1] : reference
      const displayTitle = title.length > 10 ? title.substring(0, 10) + "..." : title
      if (parts.length === 2) {
        const [boardName, postTitle] = parts
        return `<a href="/api/v1/posts/search?q=${encodeURIComponent(postTitle)}&board_name=${encodeURIComponent(boardName)}"
                   class="post-reference text-purple-600 hover:text-purple-800 underline cursor-pointer"
                   data-board="${this.escapeHtml(boardName)}"
                   data-title="${this.escapeHtml(postTitle)}"
                   data-action="click->chat-room#navigateToPost">${this.escapeHtml(displayTitle)}</a>`
      }
      return `<span class="post-reference text-purple-600">${this.escapeHtml(displayTitle)}</span>`
    })

    // Auto-link bare URLs
    formatted = formatted.replace(/(?<![="'\/])https?:\/\/[^\s<>"{}|\\^\[\]&]+/g, (url) => {
      return `<a href="${url}" class="underline opacity-90 hover:opacity-100 break-all" target="_blank" rel="noopener noreferrer">${url}</a>`
    })

    return formatted
  }

  confirmDownload(event) {
    const filename = event.currentTarget.dataset.filename || "파일"
    if (!confirm(`"${filename}" 파일을 다운로드하시겠습니까?`)) {
      event.preventDefault()
    }
  }

  renderMessageBody(message, isMine) {
    const type = message.message_type
    if (type === "image" && message.file_url) {
      const fname = this.escapeHtml(message.file_name || "image")
      const url = this.escapeHtml(message.file_url)
      return `<div class="rounded-lg overflow-hidden relative group/img">
        <img src="${url}" alt="${fname}" class="max-w-full h-auto" loading="lazy">
        <a href="${url}" download="${fname}"
           class="absolute bottom-2 right-2 opacity-0 group-hover/img:opacity-100 transition-opacity bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5"
           data-action="click->chat-room#confirmDownload" data-filename="${fname}" title="다운로드">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
        </a>
      </div>`
    }
    if (type === "file" && message.file_url) {
      const fname = this.escapeHtml(message.file_name || "파일")
      const url = this.escapeHtml(message.file_url)
      const iconColor = isMine ? "text-red-100" : "text-gray-400"
      const bgColor = isMine ? "bg-red-400" : "bg-gray-50 dark:bg-gray-700"
      return `<a href="${url}" download="${fname}"
          class="flex items-center space-x-3 p-2 rounded-lg cursor-pointer hover:opacity-80 ${bgColor}"
          data-action="click->chat-room#confirmDownload" data-filename="${fname}" title="클릭하여 다운로드">
          <svg class="h-8 w-8 ${iconColor}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium truncate">${fname}</p>
          </div>
          <svg class="h-5 w-5 flex-shrink-0 ${iconColor}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
        </a>`
    }
    const formattedContent = this.formatMessageContent(message.content, {})
    return `<p class="text-sm whitespace-pre-wrap break-words">${formattedContent}</p>`
  }

  replyToMessage(event) {
    const messageId = event.currentTarget.dataset.messageId
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`)

    if (messageElement) {
      // Get message content preview
      const contentEl = messageElement.querySelector(".text-sm.whitespace-pre-wrap")
      const senderEl = messageElement.querySelector("p.text-xs.text-gray-500.mb-1.font-medium")

      const content = contentEl?.textContent?.trim() || ""
      const sender = senderEl?.textContent?.trim() || messageElement.dataset.senderId

      // Show reply preview above input (calls hideReplyPreview internally to clear old state)
      this.showReplyPreview(sender, content)

      // Store reply info AFTER showReplyPreview (which clears previous reply state)
      this.replyToId = messageId
      this.replyToSender = sender
      this.replyToContent = content.substring(0, 50)

      // Focus input
      this.inputTarget.focus()
    }
  }

  showReplyPreview(sender, content) {
    // Remove existing preview if any
    this.hideReplyPreview()

    const preview = document.createElement("div")
    preview.id = "reply-preview"
    preview.className = "flex items-center justify-between px-3 py-2 bg-gray-100 border-l-4 border-red-500 text-sm"
    preview.innerHTML = `
      <div class="flex-1 min-w-0">
        <span class="font-medium text-red-600">${this.escapeHtml(sender)}</span>
        <span class="text-gray-600 truncate block">${this.escapeHtml(content.substring(0, 50))}${content.length > 50 ? '...' : ''}</span>
      </div>
      <button type="button" class="ml-2 text-gray-400 hover:text-gray-600" data-action="click->chat-room#hideReplyPreview">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `

    // Insert before the form
    const form = this.inputTarget.closest("form")
    form.parentElement.insertBefore(preview, form)
  }

  hideReplyPreview() {
    const preview = document.getElementById("reply-preview")
    if (preview) preview.remove()
    this.replyToId = null
    this.replyToSender = null
    this.replyToContent = null
  }

  toggleReactionPicker(event) {
    event.stopPropagation()
    const messageId = event.currentTarget.dataset.messageId

    // Close any other open pickers, toggle this one
    const picker = this.element.querySelector(`.reaction-picker[data-message-id="${messageId}"]`)
    const wasHidden = picker?.classList.contains("hidden")
    this.closeAllReactionPickers()

    if (picker && wasHidden) {
      picker.classList.remove("hidden")
    }
  }

  async selectReaction(event) {
    event.stopPropagation()
    const emoji = event.currentTarget.dataset.emoji
    const messageId = event.currentTarget.dataset.messageId

    this.closeAllReactionPickers()

    const isActive = this.reactions[messageId]?.[emoji]?.mine
    const method = isActive ? "DELETE" : "POST"
    const url = isActive
      ? `/api/v1/chat/messages/${messageId}/unreact`
      : `/api/v1/chat/messages/${messageId}/react`

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content
        },
        body: JSON.stringify({ reaction_type: emoji })
      })

      if (response.ok) {
        const data = await response.json()
        this.updateReactionsFromServer(messageId, data.reactions, data.my_reactions)
      }
    } catch (error) {
      console.error("Failed to send reaction:", error)
    }
  }

  updateReactionsFromServer(messageId, reactionCounts, myReactions) {
    const updated = {}
    for (const [emoji, count] of Object.entries(reactionCounts || {})) {
      updated[emoji] = { count, mine: (myReactions || []).includes(emoji) }
    }
    if (Object.keys(updated).length > 0) {
      this.reactions[messageId] = updated
    } else {
      delete this.reactions[messageId]
    }
    this.updateReactionsDisplay(messageId)
  }

  onReactionUpdate(messageId, reactions) {
    const current = this.reactions[messageId] || {}
    const updated = {}
    for (const [emoji, count] of Object.entries(reactions || {})) {
      updated[emoji] = { count, mine: current[emoji]?.mine || false }
    }
    if (Object.keys(updated).length > 0) {
      this.reactions[messageId] = updated
    } else {
      delete this.reactions[messageId]
    }
    this.updateReactionsDisplay(messageId)
  }

  initializeReactionsFromDOM() {
    this.element.querySelectorAll(".reactions-display[data-reactions-for]").forEach(container => {
      const messageId = container.dataset.reactionsFor
      const buttons = container.querySelectorAll("[data-emoji]")
      if (buttons.length === 0) return

      this.reactions[messageId] = {}
      buttons.forEach(btn => {
        const emoji = btn.dataset.emoji
        const countEl = btn.querySelector(".reaction-count")
        const count = countEl ? parseInt(countEl.textContent) || 1 : 1
        const mine = btn.dataset.mine === "true"
        this.reactions[messageId][emoji] = { count, mine }
      })
    })
  }

  updateReactionsDisplay(messageId) {
    const container = this.element.querySelector(`.reactions-display[data-reactions-for="${messageId}"]`)
    if (!container) return

    const reactions = this.reactions[messageId] || {}
    const entries = Object.entries(reactions)

    if (entries.length === 0) {
      container.innerHTML = ""
      return
    }

    container.innerHTML = entries.map(([emoji, data]) =>
      `<button type="button"
              class="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-sm cursor-pointer transition-colors border shadow-sm ${data.mine ? 'bg-red-50 border-red-300 hover:bg-red-100' : 'bg-gray-100 border-gray-200 hover:bg-gray-200'}"
              data-action="click->chat-room#selectReaction"
              data-emoji="${this.escapeHtml(emoji)}"
              data-message-id="${messageId}"
              data-mine="${data.mine}">
        <span>${emoji}</span>
        ${data.count > 1 ? `<span class="text-xs text-gray-600 reaction-count">${data.count}</span>` : ""}
      </button>`
    ).join("")
  }

  closeAllReactionPickers() {
    this.element.querySelectorAll(".reaction-picker").forEach(picker => {
      picker.classList.add("hidden")
    })
  }

  toggleDeleteMenu(event) {
    event.stopPropagation()
    const messageId = event.currentTarget.dataset.messageId
    const menu = this.element.querySelector(`.delete-menu[data-message-id="${messageId}"]`)
    if (!menu) return
    const wasHidden = menu.classList.contains("hidden")
    this.closeAllDeleteMenus()
    if (wasHidden) menu.classList.remove("hidden")
  }

  closeAllDeleteMenus() {
    this.element.querySelectorAll(".delete-menu").forEach(m => m.classList.add("hidden"))
  }

  async hideForMe(event) {
    const messageId = event.currentTarget.dataset.messageId
    this.closeAllDeleteMenus()
    if (!confirm("이 메시지를 나에게만 삭제하시겠습니까?")) return

    try {
      const response = await fetch(`/chat/messages/${messageId}/hide`, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content }
      })
      if (response.ok) {
        const msgEl = document.querySelector(`[data-message-id="${messageId}"]`)
        if (msgEl) msgEl.remove()
      }
    } catch (error) {
      console.error("Failed to hide message:", error)
    }
  }

  async deleteForAll(event) {
    const messageId = event.currentTarget.dataset.messageId
    this.closeAllDeleteMenus()
    if (!confirm("이 메시지를 모두에게 삭제하시겠습니까?")) return

    try {
      const response = await fetch(`/chat/messages/${messageId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']")?.content,
          "Accept": "application/json"
        }
      })
      if (response.ok) {
        const msgEl = document.querySelector(`[data-message-id="${messageId}"]`)
        if (msgEl) msgEl.remove()
      }
    } catch (error) {
      console.error("Failed to delete message:", error)
    }
  }

  async navigateToPost(event) {
    event.preventDefault()
    const link = event.currentTarget
    const boardName = link.dataset.board
    const title = link.dataset.title

    try {
      // Search for the post
      const params = new URLSearchParams()
      params.append("q", title)
      if (boardName) params.append("board", boardName)
      params.append("limit", "1")

      const response = await fetch(`/api/v1/posts/search?${params.toString()}`)
      if (response.ok) {
        const posts = await response.json()
        if (posts.length > 0) {
          const post = posts[0]
          window.location.href = `/boards/${post.bo_table}/posts/${post.wr_id}`
          return
        }
      }
      alert("게시글을 찾을 수 없습니다.")
    } catch (error) {
      console.error("Failed to navigate to post:", error)
      alert("게시글을 찾을 수 없습니다.")
    }
  }

  onPresence(mbId, status, onlineMembers) {
    this.onlineMembers = new Set(onlineMembers || [])
    // Re-run unread recalculation with cached read times
    if (this.lastReadTimes) {
      this.onReadUpdate(this.lastReadTimes)
    }
  }

  // Called when Turbo Stream replaces the hidden unread-data element
  unreadDataTargetConnected(element) {
    try {
      const readTimes = JSON.parse(element.dataset.readTimes || "[]")
      const online = JSON.parse(element.dataset.online || "[]")
      this.onlineMembers = new Set(online)
      this.onReadUpdate(readTimes, online)
    } catch (e) {
      console.error("Failed to parse unread data:", e)
    }
  }

  // --- Context Menu (right-click / long-press) ---

  handleContextMenu(e) {
    const msgEl = e.target.closest(".chat-message")
    if (!msgEl) return
    e.preventDefault()
    this.showReadStatusModal(msgEl.dataset.messageId)
  }

  handleTouchStart(e) {
    const msgEl = e.target.closest(".chat-message")
    if (!msgEl) return
    const messageId = msgEl.dataset.messageId
    this.longPressTimer = setTimeout(() => {
      this.showReadStatusModal(messageId)
    }, 600)
  }

  handleTouchEnd() {
    clearTimeout(this.longPressTimer)
  }

  async showReadStatusModal(messageId) {
    try {
      const response = await fetch(`/chat/messages/${messageId}/read_status`, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) return

      const data = await response.json()
      this.renderReadStatusModal(data)
    } catch (err) {
      console.error("Failed to fetch read status:", err)
    }
  }

  renderReadStatusModal(data) {
    this.closeReadStatusModal()

    const overlay = document.createElement("div")
    overlay.id = "read-status-overlay"
    overlay.className = "fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) this.closeReadStatusModal()
    })

    const renderMember = (m, showRead) => {
      const statusDot = m.online
        ? '<span class="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></span>'
        : '<span class="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0"></span>'

      const readTime = showRead && m.last_read_at ? m.last_read_at : ''
      const onlineText = m.online ? '<span class="text-green-500 text-xs font-medium">Online</span>' : ''
      const joinedText = m.joined_at ? `<span class="text-xs text-gray-400">입장 ${m.joined_at}</span>` : ''

      return `
        <div class="flex items-center justify-between py-2.5 px-1">
          <div class="flex items-center gap-2.5 min-w-0">
            ${statusDot}
            <span class="text-sm font-medium text-gray-900 dark:text-white truncate">${this.escapeHtml(m.nickname)}</span>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0 text-right">
            ${onlineText}
            ${joinedText}
            ${readTime ? `<span class="text-xs text-gray-400">읽음 ${readTime}</span>` : ''}
          </div>
        </div>
      `
    }

    overlay.innerHTML = `
      <div class="bg-white dark:bg-gray-800 w-full sm:w-96 sm:rounded-xl rounded-t-xl max-h-[70vh] overflow-hidden shadow-2xl">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-base font-bold text-gray-900 dark:text-white">메시지 읽음 상태</h3>
          <button type="button" class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" id="close-read-status">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="overflow-y-auto max-h-[calc(70vh-60px)]">
          ${data.read.length > 0 ? `
            <div class="px-5 pt-3 pb-1">
              <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">읽음 <span class="text-blue-500">${data.read.length}</span></p>
            </div>
            <div class="px-4 divide-y divide-gray-100 dark:divide-gray-700">
              ${data.read.map(m => renderMember(m, true)).join('')}
            </div>
          ` : ''}
          ${data.unread.length > 0 ? `
            <div class="px-5 pt-3 pb-1">
              <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">안읽음 <span class="text-red-500">${data.unread.length}</span></p>
            </div>
            <div class="px-4 divide-y divide-gray-100 dark:divide-gray-700">
              ${data.unread.map(m => renderMember(m, false)).join('')}
            </div>
          ` : ''}
          ${data.read.length === 0 && data.unread.length === 0 ? `
            <div class="px-5 py-8 text-center text-sm text-gray-400">참가자 정보가 없습니다.</div>
          ` : ''}
        </div>
      </div>
    `

    document.body.appendChild(overlay)
    overlay.querySelector("#close-read-status").addEventListener("click", () => this.closeReadStatusModal())
  }

  closeReadStatusModal() {
    const overlay = document.getElementById("read-status-overlay")
    if (overlay) overlay.remove()
  }

  onReadUpdate(readTimes, onlineMembers) {
    // Update online members if provided (included in every read_update)
    if (onlineMembers) {
      this.onlineMembers = new Set(onlineMembers)
    }
    // Cache read times for recalculation on presence changes
    this.lastReadTimes = readTimes
    // Recalculate unread counts for all visible messages
    const messages = this.element.querySelectorAll("[data-message-id]")
    messages.forEach(msgEl => {
      const messageId = msgEl.dataset.messageId
      const senderId = msgEl.dataset.senderId
      const timeEl = msgEl.querySelector(`[data-unread-count="${messageId}"]`)
      const createdAt = msgEl.dataset.createdAt
      if (!createdAt) return

      let unread = 0
      readTimes.forEach(p => {
        if (p.mb_id !== senderId) {
          // Online members (in the room) are considered as having read
          const isOnline = this.onlineMembers.has(p.mb_id)
          if (!isOnline && (!p.last_read_at || new Date(p.last_read_at) < new Date(createdAt))) {
            unread++
          }
        }
      })

      if (timeEl) {
        if (unread > 0) {
          timeEl.textContent = unread > 999 ? "999+" : unread
        } else {
          timeEl.remove()
        }
      } else if (unread > 0) {
        // Add new unread badge
        const timestampDiv = msgEl.querySelector(".flex.items-center.mt-1")
        if (timestampDiv) {
          const badge = document.createElement("span")
          badge.className = "text-xs font-medium text-red-500"
          badge.dataset.unreadCount = messageId
          badge.textContent = unread > 999 ? "999+" : unread
          timestampDiv.insertBefore(badge, timestampDiv.firstChild)
        }
      }
    })
  }

  scrollToBottom() {
    if (!this.hasMessagesTarget) return

    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
